import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || (
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:5555'
    : window.location.origin
);

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  functional: boolean;
  consentDate?: string;
}

class CookiePreferencesService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async fetchPreferences(): Promise<CookiePreferences | null> {
    try {
      const response = await axios.get(
        `${API_URL}/api/cookie-preferences`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching cookie preferences:', error);
      return null;
    }
  }

  async savePreferences(preferences: Omit<CookiePreferences, 'essential' | 'consentDate'>): Promise<boolean> {
    try {
      await axios.post(
        `${API_URL}/api/cookie-preferences`,
        preferences,
        { headers: this.getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
      return false;
    }
  }

  // Sync local storage with database for logged-in users
  async syncWithDatabase(): Promise<CookiePreferences | null> {
    const token = localStorage.getItem('token');
    if (!token) return null;

    // Try to fetch from database first
    const dbPreferences = await this.fetchPreferences();
    
    if (dbPreferences && dbPreferences.consentDate) {
      // User has saved preferences in database, use those
      localStorage.setItem('cookieConsent', JSON.stringify(dbPreferences));
      return dbPreferences;
    } else {
      // No database preferences, check localStorage
      const localConsent = localStorage.getItem('cookieConsent');
      if (localConsent) {
        try {
          const localPreferences = JSON.parse(localConsent);
          // Save to database
          await this.savePreferences({
            analytics: localPreferences.analytics || false,
            functional: localPreferences.functional || false
          });
          return localPreferences;
        } catch (e) {
          console.error('Error parsing local preferences:', e);
        }
      }
    }
    
    return null;
  }
}

export const cookiePreferencesService = new CookiePreferencesService();